/**
 * Fit the Frontline board into the mid band between measured HUD chrome.
 * Keeps square cells; falls back to catalog metrics when insets are unusable.
 */

export type FrontlineBoardMetrics = {
  originX: number;
  originY: number;
  cellW: number;
  cellH: number;
};

const MIN_CELL = 28;

export function computeFrontlineBoardLayout(opts: {
  gameW: number;
  gameH: number;
  topInset: number;
  bottomInset: number;
  cols: number;
  rows: number;
  fallback: FrontlineBoardMetrics;
  /** Optional side gutter in game px (default 8). */
  sidePad?: number;
  minCell?: number;
}): FrontlineBoardMetrics {
  const {
    gameW,
    gameH,
    cols,
    rows,
    fallback,
    sidePad = 8,
    minCell = MIN_CELL,
  } = opts;
  const top = Math.max(0, opts.topInset);
  const bottom = Math.max(0, opts.bottomInset);
  const availH = gameH - top - bottom;
  const availW = gameW - sidePad * 2;
  if (availH < minCell * rows || availW < minCell * cols || cols <= 0 || rows <= 0) {
    return { ...fallback };
  }
  const cell = Math.max(
    minCell,
    Math.min(availW / cols, availH / rows, fallback.cellW, fallback.cellH),
  );
  const boardW = cell * cols;
  const boardH = cell * rows;
  const originX = (gameW - boardW) * 0.5;
  const originY = top + Math.max(0, (availH - boardH) * 0.5);
  return {
    originX,
    originY,
    cellW: cell,
    cellH: cell,
  };
}

export function boardLayoutChanged(
  a: FrontlineBoardMetrics,
  b: FrontlineBoardMetrics,
  eps = 0.5,
): boolean {
  return (
    Math.abs(a.originX - b.originX) > eps ||
    Math.abs(a.originY - b.originY) > eps ||
    Math.abs(a.cellW - b.cellW) > eps ||
    Math.abs(a.cellH - b.cellH) > eps
  );
}
